import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  isChunkLoadError,
  hasAttemptedChunkRecovery,
  recoverFromChunkError,
} from '@/lib/chunkLoadRecovery';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  recoveryInProgress: boolean;
}

/**
 * Error Boundary global (rutas del Admin incluidas).
 *
 * - Si el error es de chunk obsoleto → intenta UNA sola recuperación
 *   automática (recarga para traer el build vigente). Nunca hace loops.
 * - Si es un error inesperado de UI → NO recarga: muestra un panel de error
 *   controlado con "Reintentar" (re-render) y "Recargar".
 * - En ningún caso deja la pantalla en blanco.
 */
export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, recoveryInProgress: false };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error, recoveryInProgress: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary] Unexpected UI error:', error, errorInfo);

    if (isChunkLoadError(error)) {
      const reloadTriggered = recoverFromChunkError();
      this.setState({ recoveryInProgress: reloadTriggered });
    }
  }

  handleTryAgain = (): void => {
    this.setState({ hasError: false, error: null, recoveryInProgress: false });
  };

  handleReload = (): void => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const chunkRelated = isChunkLoadError(this.state.error);
    const alreadyRecovered = chunkRelated && hasAttemptedChunkRecovery();

    const title = chunkRelated
      ? alreadyRecovered
        ? 'No se pudo cargar la actualización'
        : 'Actualización disponible'
      : 'Algo salió mal';

    const description = chunkRelated
      ? 'La aplicación se actualizó en el servidor y esta pestaña tenía una versión anterior. Recarga para continuar donde estabas.'
      : 'Ocurrió un error inesperado en esta sección. Puedes reintentar o recargar la página. Tus datos están a salvo.';

    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-md bg-background-100 border border-background-200 rounded-lg p-6 md:p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-lg bg-primary-100 flex items-center justify-center mb-5">
            <i
              className={`${chunkRelated ? 'ri-refresh-line' : 'ri-error-warning-line'} text-2xl text-primary-700`}
            ></i>
          </div>

          <h1 className="text-lg md:text-xl font-semibold text-foreground-950 mb-2">
            {title}
          </h1>
          <p className="text-sm text-foreground-700 leading-relaxed mb-6">
            {description}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-base"></i>
              Recargar
            </button>
            {!chunkRelated && (
              <button
                type="button"
                onClick={this.handleTryAgain}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line text-base"></i>
                Reintentar
              </button>
            )}
          </div>

          {chunkRelated && alreadyRecovered && (
            <p className="text-xs text-foreground-500 mt-5 leading-snug">
              Si el problema continúa, cierra esta pestaña y vuelve a abrir el Admin.
            </p>
          )}
        </div>
      </div>
    );
  }
}